import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Button, Typography, Table } from 'antd';
import { UserOutlined, BankOutlined, TeamOutlined, ArrowRightOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { customerService } from '../services/customers';
import { businessService } from '../services/businesses';
import { staffService } from '../services/staffs';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';

const { Title, Text } = Typography;

export const People: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreatePersonal = hasPermission(user, 'personal.create');
  const canCreateBusiness = hasPermission(user, 'business.create');
  const canCreateStaff = hasPermission(user, 'staff.create');
  const canViewPersonal = hasPermission(user, 'personal.view');
  const canViewBusiness = hasPermission(user, 'business.view');
  const canViewStaff = hasPermission(user, 'staff.view');
  const [stats, setStats] = useState({
    personal: {
      total: 0,
      potential: 0,
      active: 0,
      inactive: 0,
      byCategory: [] as Array<{ category: string; potential: number; active: number; inactive: number }>,
    },
    business: {
      total: 0,
      soleProprietorship: {
        potential: 0,
        active: 0,
        inactive: 0,
      },
      sdnBhd: {
        potential: 0,
        active: 0,
        inactive: 0,
      },
      partnership: {
        potential: 0,
        active: 0,
        inactive: 0,
      },
      llp: {
        potential: 0,
        active: 0,
        inactive: 0,
      },
    },
    staff: {
      total: 0,
      active: 0,
      inactive: 0,
      byRole: [] as Array<{ role: string; active: number; inactive: number }>,
    },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const [customersResponse, businessesResponse, staffsResponse] = await Promise.all([
        canViewPersonal ? customerService.getAll() : Promise.resolve({ data: [] }),
        canViewBusiness ? businessService.getAll() : Promise.resolve({ data: [] }),
        canViewStaff ? staffService.getAll() : Promise.resolve([]),
      ]);

      const customers = canViewPersonal ? customersResponse.data || [] : [];
      const businesses = canViewBusiness ? businessesResponse.data || [] : [];
      const staffs = canViewStaff && Array.isArray(staffsResponse) ? staffsResponse : [];

      // Calculate Personal stats (customers)
      const personalTotal = customers.length;
      const personalPotential = customers.filter((c: any) => c.type === 'potential').length;
      const personalActive = customers.filter((c: any) => c.type === 'active' || c.type === 'existing').length;
      const personalInactive = customers.filter((c: any) => c.type === 'inactive' || c.type === 'resigned').length;

      // Group Personal by engagement (B, BE, etc.)
      const personalByEngagement: { [key: string]: { potential: number; active: number; inactive: number } } = {};
      customers.forEach((c: any) => {
        const engagement = c.engagement || 'Other';
        if (!personalByEngagement[engagement]) {
          personalByEngagement[engagement] = { potential: 0, active: 0, inactive: 0 };
        }
        if (c.type === 'potential') {
          personalByEngagement[engagement].potential++;
        } else if (c.type === 'active' || c.type === 'existing') {
          personalByEngagement[engagement].active++;
        } else if (c.type === 'inactive' || c.type === 'resigned') {
          personalByEngagement[engagement].inactive++;
        }
      });
      const personalByCategory = Object.entries(personalByEngagement).map(([category, counts]) => ({
        category,
        ...counts,
      }));

      // Calculate Business stats (by entity and type)
      const businessTotal = businesses.length;
      
      // Sole Proprietorship
      const soleProprietorshipBusinesses = businesses.filter((b: any) => b.entity === 'Sole Proprietorship');
      const soleProprietorshipPotential = soleProprietorshipBusinesses.filter((b: any) => b.type === 'potential').length;
      const soleProprietorshipActive = soleProprietorshipBusinesses.filter((b: any) => b.type === 'active').length;
      const soleProprietorshipInactive = soleProprietorshipBusinesses.filter((b: any) => b.type === 'inactive').length;

      // Private Limited Company (Sdn Bhd)
      const sdnBhdBusinesses = businesses.filter((b: any) => b.entity === 'Private Limited Company');
      const sdnBhdPotential = sdnBhdBusinesses.filter((b: any) => b.type === 'potential').length;
      const sdnBhdActive = sdnBhdBusinesses.filter((b: any) => b.type === 'active').length;
      const sdnBhdInactive = sdnBhdBusinesses.filter((b: any) => b.type === 'inactive').length;
      
      // Partnership
      const partnershipBusinesses = businesses.filter((b: any) => b.entity === 'Partnership');
      const partnershipPotential = partnershipBusinesses.filter((b: any) => b.type === 'potential').length;
      const partnershipActive = partnershipBusinesses.filter((b: any) => b.type === 'active').length;
      const partnershipInactive = partnershipBusinesses.filter((b: any) => b.type === 'inactive').length;
      
      // LLP
      const llpBusinesses = businesses.filter((b: any) => b.entity === 'LLP');
      const llpPotential = llpBusinesses.filter((b: any) => b.type === 'potential').length;
      const llpActive = llpBusinesses.filter((b: any) => b.type === 'active').length;
      const llpInactive = llpBusinesses.filter((b: any) => b.type === 'inactive').length;
      
      // Calculate User stats by role
      const targetRoles = ['admin', 'junior', 'senior', 'manager'];
      
      // Initialize role counters
      const staffByRole: { [key: string]: { active: number; inactive: number } } = {
        admin: { active: 0, inactive: 0 },
        junior: { active: 0, inactive: 0 },
        senior: { active: 0, inactive: 0 },
        manager: { active: 0, inactive: 0 },
      };
      
      // Count staff by role
      staffs.forEach((s: any) => {
        const roleName = s.role?.name?.toLowerCase();
        // Only count if role is one of the target roles
        if (roleName && targetRoles.includes(roleName)) {
        if (s.type === 'active') {
            staffByRole[roleName].active++;
        } else {
            staffByRole[roleName].inactive++;
          }
        }
      });
      
      // Calculate totals for staff with target roles
      const staffTotal = Object.values(staffByRole).reduce((sum, counts) => sum + counts.active + counts.inactive, 0);
      const staffActive = Object.values(staffByRole).reduce((sum, counts) => sum + counts.active, 0);
      const staffInactive = Object.values(staffByRole).reduce((sum, counts) => sum + counts.inactive, 0);
      
      const staffByRoleArray = Object.entries(staffByRole)
        .map(([role, counts]) => ({
        role,
          ...counts,
        }));

      setStats({
        personal: {
          total: personalTotal,
          potential: personalPotential,
          active: personalActive,
          inactive: personalInactive,
          byCategory: personalByCategory,
        },
        business: {
          total: businessTotal,
          soleProprietorship: {
            potential: soleProprietorshipPotential,
            active: soleProprietorshipActive,
            inactive: soleProprietorshipInactive,
          },
          sdnBhd: {
            potential: sdnBhdPotential,
            active: sdnBhdActive,
            inactive: sdnBhdInactive,
          },
          partnership: {
            potential: partnershipPotential,
            active: partnershipActive,
            inactive: partnershipInactive,
          },
          llp: {
            potential: llpPotential,
            active: llpActive,
            inactive: llpInactive,
          },
        },
        staff: {
          total: staffTotal,
          active: staffActive,
          inactive: staffInactive,
          byRole: staffByRoleArray,
        },
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Title level={3} style={{ margin: 0, marginBottom: 16 }}>People</Title>

      <Row gutter={[24, 24]} align="stretch">
        {/* Personal Card */}
        {canViewPersonal && (
        <Col xs={24} md={8}>
          <Card
            style={{
              borderRadius: '16px',
              background: 'white',
              border: 'none',
              height: '100%',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
            bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', flex: 1 }}
          >
            {/* Top Section with Background Color */}
            <div style={{
              background: '#DBEAFE',
              padding: '24px',
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '12px',
                    background: '#BBDEFB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <UserOutlined style={{ fontSize: '28px', color: 'white' }} />
                  </div>
                  <Title level={3} style={{ color: '#424242', margin: 0, fontWeight: 700 }}>Personal</Title>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: '#1976D2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '28px',
                    fontWeight: 700,
                    color: 'white',
                    marginLeft: 'auto',
                  }}>
                    {stats.personal.total}
                  </div>
                  <Text style={{ color: '#1976D2', fontSize: '12px', marginTop: '8px', display: 'block' }}>Total Personal</Text>
                </div>
              </div>
            </div>

            {/* Bottom Section with White Background */}
            <div style={{
              background: 'white',
              padding: '24px',
              borderBottomLeftRadius: '16px',
              borderBottomRightRadius: '16px',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
            }}>
              {/* Table - Status Summary */}
              <div style={{ marginBottom: '24px' }}>
                <Table
                  dataSource={[
                    {
                      category: 'Total',
                      potential: stats.personal.potential,
                      active: stats.personal.active,
                      inactive: stats.personal.inactive,
                    },
                  ]}
                  columns={[
    {
      title: '',
      dataIndex: 'category',
      key: 'category',
                      render: (text: string) => (
                        <Text strong style={{ color: '#1976D2' }}>
                          {text}
                        </Text>
      ),
    },
    {
      title: 'Potential',
      dataIndex: 'potential',
      key: 'potential',
      align: 'center' as const,
                      render: (value: number) => (
                        <Text style={{ color: '#1976D2', fontWeight: 600 }}>
                          {value}
                        </Text>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      align: 'center' as const,
                      render: (value: number) => (
                        <Text style={{ color: '#1976D2', fontWeight: 600 }}>
                          {value}
                        </Text>
      ),
    },
    {
      title: 'Inactive',
      dataIndex: 'inactive',
      key: 'inactive',
      align: 'center' as const,
                      render: (value: number) => (
                        <Text style={{ color: '#1976D2', fontWeight: 600 }}>
                          {value}
                        </Text>
                      ),
                    },
                  ]}
                  pagination={false}
                  size="small"
                  style={{
                    border: '1px solid #E0E0E0',
                    borderRadius: '8px',
                  }}
                  rowKey="category"
                />
              </div>

              {/* Buttons - Stacked Vertically */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: 'auto' }}>
                <Button
                  icon={<ArrowRightOutlined />}
                  onClick={() => navigate('/customers')}
                  style={{
                    width: '100%',
                    background: '#BBDEFB',
                    color: '#424242',
                    border: 'none',
                    borderRadius: '10px',
                    height: '44px',
                    fontWeight: 600,
                    fontSize: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  View All Personal
                </Button>
                {canCreatePersonal && (
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => navigate('/customers/new')}
                    style={{
                      width: '100%',
                      background: 'white',
                      color: '#424242',
                      border: '1px solid #E0E0E0',
                      borderRadius: '10px',
                      height: '44px',
                      fontWeight: 600,
                      fontSize: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    Add Personal
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </Col>
        )}

        {/* Business Card */}
        {canViewBusiness && (
        <Col xs={24} md={8} >
          <Card
            style={{
              borderRadius: '16px',
              background: 'white',
              border: 'none',
              height: '100%',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
            bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', flex: 1 }}
          >
            {/* Top Section with Background Color */}
            <div style={{
              background: '#D0FAE5',
              padding: '24px',
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '12px',
                    background: '#C8E6C9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <BankOutlined style={{ fontSize: '28px', color: 'white' }} />
                  </div>
                  <Title level={3} style={{ color: '#424242', margin: 0, fontWeight: 700 }}>Business</Title>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: '#388E3C',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '28px',
                    fontWeight: 700,
                    color: 'white',
                    marginLeft: 'auto',
                  }}>
                    {stats.business.total}
                  </div>
                  <Text style={{ color: '#388E3C', fontSize: '12px', marginTop: '8px', display: 'block' }}>Total Business</Text>
                </div>
              </div>
            </div>

            {/* Bottom Section with White Background */}
            <div style={{
              background: 'white',
              padding: '24px',
              borderBottomLeftRadius: '16px',
              borderBottomRightRadius: '16px',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
            }}>
              {/* Table - Status by Business Type */}
              <div style={{ marginBottom: '24px', overflowX: 'auto' }}>
                <Table
                  dataSource={[
                    {
                      type: 'Sole Proprietorship',
                      potential: stats.business.soleProprietorship.potential,
                      active: stats.business.soleProprietorship.active,
                      inactive: stats.business.soleProprietorship.inactive,
                    },
                    {
                      type: 'Partnership',
                      potential: stats.business.partnership.potential,
                      active: stats.business.partnership.active,
                      inactive: stats.business.partnership.inactive,
                    },
                    {
                      type: 'Private Limited Company',
                      potential: stats.business.sdnBhd.potential,
                      active: stats.business.sdnBhd.active,
                      inactive: stats.business.sdnBhd.inactive,
                    },
                    {
                      type: 'Limited Liability Partnership',
                      potential: stats.business.llp.potential,
                      active: stats.business.llp.active,
                      inactive: stats.business.llp.inactive,
                    },
                    {
                      type: 'Total',
                      potential: stats.business.soleProprietorship.potential + stats.business.partnership.potential + stats.business.sdnBhd.potential + stats.business.llp.potential,
                      active: stats.business.soleProprietorship.active + stats.business.partnership.active + stats.business.sdnBhd.active + stats.business.llp.active,
                      inactive: stats.business.soleProprietorship.inactive + stats.business.partnership.inactive + stats.business.sdnBhd.inactive + stats.business.llp.inactive,
                    },
                  ]}
                  columns={[
    {
      title: '',
      dataIndex: 'type',
      key: 'type',
                      render: (text: string) => (
                        <Text strong={text === 'Total'} style={{ color: text === 'Total' ? '#388E3C' : '#424242' }}>
                          {text}
                        </Text>
      ),
    },
    {
      title: 'Potential',
      dataIndex: 'potential',
      key: 'potential',
      align: 'center' as const,
                      render: (value: number, record: any) => (
                        <Text style={{ color: '#388E3C', fontWeight: record.type === 'Total' ? 600 : 400 }}>
                          {value}
                        </Text>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      align: 'center' as const,
                      render: (value: number, record: any) => (
                        <Text style={{ color: '#388E3C', fontWeight: record.type === 'Total' ? 600 : 400 }}>
                          {value}
                        </Text>
      ),
    },
    {
      title: 'Inactive',
      dataIndex: 'inactive',
      key: 'inactive',
      align: 'center' as const,
                      render: (value: number, record: any) => (
                        <Text style={{ color: '#388E3C', fontWeight: record.type === 'Total' ? 600 : 400 }}>
                          {value}
                        </Text>
                      ),
                    },
                  ]}
                  pagination={false}
                  size="small"
                  scroll={{ x: 'max-content' }}
                  style={{
                    border: '1px solid #E0E0E0',
                    borderRadius: '8px',
                  }}
                  rowKey={(record, index) => record.type || `row-${index}`}
                />
              </div>

              {/* Buttons - Stacked Vertically */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: 'auto' }}>
                <Button
                  icon={<ArrowRightOutlined />}
                  onClick={() => navigate('/businesses')}
                  style={{
                    width: '100%',
                    background: '#C8E6C9',
                    color: '#424242',
                    border: 'none',
                    borderRadius: '10px',
                    height: '44px',
                    fontWeight: 600,
                    fontSize: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  View All Business
                </Button>
                {canCreateBusiness && (
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => navigate('/businesses/new')}
                    style={{
                      width: '100%',
                      background: 'white',
                      color: '#424242',
                      border: '1px solid #E0E0E0',
                      borderRadius: '10px',
                      height: '44px',
                      fontWeight: 600,
                      fontSize: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    Add Business
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </Col>
        )}

        {/* Staff Card */}
        {canViewStaff && (
        <Col xs={24} md={8}>
          <Card
            style={{
              borderRadius: '16px',
              background: 'white',
              border: 'none',
              height: '100%',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
            bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', flex: 1 }}
          >
            {/* Top Section with Background Color */}
            <div style={{
              background: '#FFEDD4',
              padding: '24px',
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '12px',
                    background: '#FFE0B2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <TeamOutlined style={{ fontSize: '28px', color: 'white' }} />
                  </div>
                  <Title level={3} style={{ color: '#424242', margin: 0, fontWeight: 700 }}>Staff</Title>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: '#F57C00',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '28px',
                    fontWeight: 700,
                    color: 'white',
                    marginLeft: 'auto',
                  }}>
                    {stats.staff.total}
                  </div>
                  <Text style={{ color: '#F57C00', fontSize: '12px', marginTop: '8px', display: 'block' }}>Total Members</Text>
                </div>
              </div>
            </div>

            {/* Bottom Section with White Background */}
            <div style={{
              background: 'white',
              padding: '24px',
              borderBottomLeftRadius: '16px',
              borderBottomRightRadius: '16px',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
            }}>
              {/* Table - Status by Role */}
              <div style={{ marginBottom: '24px' }}>
                <Table
                  dataSource={[
                    ...stats.staff.byRole,
                    {
                      role: 'Total',
                      active: stats.staff.active,
                      inactive: stats.staff.inactive,
                    },
                  ]}
                  columns={[
    {
      title: '',
      dataIndex: 'role',
      key: 'role',
                      render: (text: string) => (
                        <Text strong={text === 'Total'} style={{ color: text === 'Total' ? '#F57C00' : '#424242' }}>
                          {text}
                        </Text>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      align: 'center' as const,
                      render: (value: number, record: any) => (
                        <Text style={{ color: '#F57C00', fontWeight: record.role === 'Total' ? 600 : 400 }}>
                          {value}
                        </Text>
      ),
    },
    {
      title: 'Inactive',
      dataIndex: 'inactive',
      key: 'inactive',
      align: 'center' as const,
                      render: (value: number, record: any) => (
                        <Text style={{ color: '#F57C00', fontWeight: record.role === 'Total' ? 600 : 400 }}>
                          {value}
                        </Text>
                      ),
                    },
                  ]}
                pagination={false}
                size="small"
                  style={{
                    border: '1px solid #E0E0E0',
                    borderRadius: '8px',
                  }}
                  rowKey={(record, index) => record.role || `row-${index}`}
                />
                </div>

              {/* Buttons - Stacked Vertically */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: 'auto' }}>
                  <Button
                    icon={<ArrowRightOutlined />}
                    onClick={() => navigate('/staffs')}
                  style={{
                    width: '100%',
                    background: '#FFE0B2',
                    color: '#424242',
                    border: 'none',
                    borderRadius: '10px',
                    height: '44px',
                    fontWeight: 600,
                    fontSize: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  >
                  View All Users
                  </Button>
                {canCreateStaff && (
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => navigate('/staffs/new')}
                    style={{
                      width: '100%',
                      background: 'white',
                      color: '#424242',
                      border: '1px solid #E0E0E0',
                      borderRadius: '10px',
                      height: '44px',
                      fontWeight: 600,
                      fontSize: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    Add User
                  </Button>
                )}
              </div>
            </div>
            </Card>
          </Col>
        )}
        </Row>
    </div>
  );
};
